import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { MatTabGroup } from '@angular/material/tabs';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { SubSink } from 'subsink2';
import { Observable, of } from 'rxjs';
import { Pagination, TeamLoginEPService, TeamService, TenantTeamRoleContainerModel, TenantTeamRoleService } from 'blg-akaun-ts-lib';
import { filter, map, switchMap } from 'rxjs/operators';
import { ViewColumnComponent } from '../../../../../../../view-column.component';
import { PaginationComponent } from '../../../../../../../utilities/pagination/pagination.component';
import { AppConfig } from '../../../../../../../visa';
import { gridOptionsServerSide, pageFiltering, pageSorting, serverSideClear } from '../../../../../../../listing.utils';
import { PermissionFacade } from '../../../../../../../modules/permission/facades/permission.facade';
import { RolePermStates } from '../../../../../../../modules/permission/role-permissions-controller/states';
import { RolePermSelectors } from '../../../../../../../modules/permission/role-permissions-controller/selectors';
import { RolePermActions } from '../../../../../../../modules/permission/role-permissions-controller/actions';
import { TeamModel } from '../../../../../../../models/team-permissions.model';
import { PermissionStates } from '../../../../../permission-controller/root';

interface LocalState {
  deactivateReturn: boolean;
  selectedIndex: number;
}

@Component({
  selector: 'app-role-perm-team-add',
  templateUrl: './role-perm-team-add.component.html',
  styleUrls: ['./role-perm-team-add.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]

})
export class RolePermTeamAddComponent extends ViewColumnComponent {

  @ViewChild(PaginationComponent, {static: false})
  private paginationComponent: PaginationComponent;
  @ViewChild(MatTabGroup, {static: true}) matTab: MatTabGroup;

  protected readonly index = 4;
  private localState: LocalState;

  // initialise local states
  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateReturn$ = this.componentStore.select(state => state.localState.deactivateReturn);
  readonly selectedIndex$ = this.componentStore.select(state => (state.localState.selectedIndex));

  // previous index
  prevIndex: number;
  private prevLocalState: any;

  // api visa
  apiVisa = AppConfig.apiVisa;

  // subSink is to ensure all subscribe calls are unsubscribed to once the component is destroyed
  private subSink = new SubSink;

  formDisabled: boolean = true;

  // search model
  searchValue: any;

  // initialise ag grid
  gridApi;
  gridOptions = gridOptionsServerSide;
  columnsDefs;
  rowSelection = 'multiple';
  appletGuid = sessionStorage.getItem('appletGuid');
  // teams already added
  addedEntities = new Map<string, any>();
  // currently selected teams
  selectedEntities = new Map<string, any>();

  // selected role guid
  roleGuid: string;

  constructor(
    // private teamService: TeamService,
    private teamService: TeamLoginEPService,
    private tenantTeamRoleService: TenantTeamRoleService,
    private viewColFacade: PermissionFacade,
    private readonly store: Store<PermissionStates>,
    private readonly componentStore: ComponentStore<{localState: LocalState}>
  ) {
    super();
  }

  /**
   * Lifecycle method: Called when the component is first initialised.
   */
   ngOnInit() {
    this.subSink.sink = this.localState$.subscribe(a => {
      this.localState = a;
      this.componentStore.setState({localState: a});
    });
    this.subSink.sink = this.viewColFacade.prevIndex$.subscribe(resolve => this.prevIndex = resolve);
    this.subSink.sink = this.viewColFacade.prevLocalState$().subscribe(resolve => this.prevLocalState = resolve);

    // initialise column definitions
    this.columnsDefs = [
      {
        headerName: '',
        field: 'app_mst_grp_hdr.guid',
        valueFormatter: params => '',
        checkboxSelection: true,
        cellStyle: params => {
          return this.addedEntities.has(params.value) ?
          {'pointer-events': 'none', textAlign: 'left'} : {textAlign: 'left'}
        },
        floatingFilter: false,
        sortable: false,
        minWidth: 0,
      },
      {
        headerName: 'Team Code',
        field: 'app_mst_grp_hdr.code',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: 'Team Name',
        field: 'app_mst_grp_hdr.name',
        cellStyle: {textAlign: 'left'}
      }
    ];

    this.subSink.sink = this.store.select(RolePermSelectors.selectGuid)
    .subscribe(x => {
      this.roleGuid = x;

      // get all current teams in role
      this.subSink.sink = this.getTeamsInRole().subscribe(x => {
        // add team links
        x.forEach(a => {
          this.addedEntities.set(a.app_mst_link_grp_to_role.mst_grp_guid.toString(), a.app_mst_link_grp_to_role);
        })
      })
    })
  }

  /**
   * Function called when AG grid is initialised.
   *
   * @param params : grid parameters
   */
   onGridReady(params) {
    this.gridApi = params.api;
    this.gridApi.closeToolPanel();
    const datasource = this.getDatasource();
    this.gridApi.setServerSideDatasource(datasource);

    // resize columns
    const allColIds = params.columnApi.getAllColumns().map(x => x.colId);
    params.columnApi.autoSizeColumns(allColIds);

    this.subSink.sink = this.store.select(RolePermSelectors.selectUpdateTeamRoleListing)
    .subscribe(a => {
      if (a) {
        // get all current teams in role again
        this.subSink.sink = this.getTeamsInRole().subscribe(x => {
          this.addedEntities.clear();
          // add teams
          x.forEach(a => {
            this.addedEntities.set(a.app_mst_link_grp_to_role.mst_grp_guid.toString(), a.app_mst_link_grp_to_role);
          });
          this.gridApi.refreshServerSideStore(
            {purge: true}
          );
          this.store.dispatch(RolePermActions.setUpdateTeamRoleListing({update: false}));
        })
      }
    })
  }

  /**
   * Returns the datasource for the AG grid.
   *
   * @param conditionalCriteria : any custom criteria to search for in the API
   * @returns the datasource
   */
   getDatasource(conditionalCriteria?) {

    let offset = 0;
    let limit = this.paginationComponent.rowPerPage;

    if (!conditionalCriteria) {
      conditionalCriteria = [
        {columnName: 'calcTotalRecords', operator: '=', value: 'true'}
      ]
    }

    return {
      getRows: grid => {

        const sortModel = grid.request.sortModel;
        const filterModel = grid.request.filterModel;
        const sortOn = pageSorting(sortModel);
        const filter = pageFiltering(filterModel);

        // only change offset and limit if no filterModel or sortModel is present
        if (Object.keys(filterModel).length == 0 && sortModel.length == 0) {
          offset = grid.request.startRow;
          limit = grid.request.endRow - offset;
        }

        const pagination = new Pagination(offset, limit, conditionalCriteria);

        this.subSink.sink = this.teamService.getByGuidCriteria(pagination, this.apiVisa, this.appletGuid.toString())
        .pipe(
          map(a => {

            const source: TeamModel[] = [];
            a.data.forEach((b) => {
              // create a new TeamModel object
              const teamModel: TeamModel = new TeamModel();
              teamModel.app_mst_grp_hdr = b.app_mst_grp_hdr;
              teamModel.app_mst_grp_lines = b.app_mst_grp_lines;
              teamModel.app_mst_grp_exts = b.app_mst_grp_exts;

              source.push(teamModel);
            })
            return {
              ...a,
              data: source
            }
          })
        ).subscribe(a => {
          // put data into the grid
          const data = sortOn(a.data).filter(entity => filter.by(entity));
          const totalRecords = filter.isFiltering ? a.totalRecords : data.length;
          // force pagination to go to first page if filter is applied
          if (this.paginationComponent.currentPage > this.paginationComponent.totalPage.value) { this.paginationComponent.firstPage() }

          grid.successCallback(data, totalRecords);
          // set selected nodes
          this.setSelectedNodes();
        }, err => {
          grid.failCallback();
        })
      }
    }
  }

  /**
   * Return an observable containing all the teams that are assigned to this role.
   *
   * @return an observable containing all the teams that are assigned to this role
   */
  getTeamsInRole(): Observable<TenantTeamRoleContainerModel[]>{
    const criteria = [
      {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
      {columnName: 'mst_role_guid', operator: '=', value: this.roleGuid}
    ];
    const pagination = new Pagination(null, null, criteria);

    return this.tenantTeamRoleService.getByCriteria(pagination, this.apiVisa)
      .pipe(
        map(x => x.data)
      )
  }

  /**
   * This function is to be called every time the grid refreshes.
   * Set nodes that are already in the permissions template or previously currently
   * selected to selected.
   *
   */
   setSelectedNodes() {
    this.gridApi.forEachNode(node => {
      if (node.data) {
        const guid = node.data.app_mst_grp_hdr.guid;
        const inRole = this.addedEntities.has(guid);
        const isCurrentlySelected = this.selectedEntities.has(guid);
        if (inRole || isCurrentlySelected) {
          node.setSelected(true);
        } else {
          node.setSelected(false);
        }
      }
    })
  }

  /**
   * This function is called when a row is either selected or deselected.
   * Updated the currentlySelected array.
   */
  onRowSelected(event) {
    const selectedGuid = (event.node) ? event.node.data.app_mst_grp_hdr.guid : null;
    const isSelectionEvent = event.node.isSelected();
    const inRole = this.addedEntities.has(selectedGuid);
    const isCurrentlySelected = this.selectedEntities.has(selectedGuid);

    if (!isSelectionEvent && isCurrentlySelected && !inRole) {
      // delete from selected entities if node is not already in the role,
      // it is a deselection event and the node is in the currently selected entities
      this.selectedEntities.delete(selectedGuid);
    } else if (isSelectionEvent && !inRole && !isCurrentlySelected) {
      // add node to the currently selected entities if it is a selection event,
      // the node is not currently already in the role and the node
      // is not in selectedEntities map already
      this.selectedEntities.set(selectedGuid, event.node.data);
    }

    if (this.selectedEntities.size > 0) {
      this.formDisabled = false;
    } else {
      this.formDisabled = true;
    }
  }

  /**
   * This function is called every time the user searches something in the
   * search bar.
   *
   * @param e : the search model
   */
   /**
   * Runs the search bar's keyword against the listing's own endpoint.
   *
   * Replaces the raw SELECT that went through SubQueryService:
   *
   *   FROM app_mst_grp_hdr hdr
   *   WHERE (hdr.code ILIKE '%x%' OR hdr.name ILIKE '%x%')
   *     AND hdr.status != 'DELETED'
   *
   * GET teams/applet-login-ep/{applet}/query -> TeamUow ->
   * generateWhereClauseByCriteria applies `search_word` as exactly
   * `code ILIKE ... OR name ILIKE ...` and defaults to `status != 'DELETED'`,
   * so this is a like-for-like replacement.
   */
  onSearch() {
    const datasource = this.getDatasource(this.buildSearchCriteria());
    this.gridApi.setServerSideDatasource(datasource);
  }

  /**
   * Criteria for the current keyword, or undefined when the box is empty so the
   * listing falls back to its default criteria.
   */
  buildSearchCriteria() {
    const keyword = (this.searchValue || '').toString().trim();
    if (!keyword) {
      return undefined;
    }
    return [
      { columnName: 'calcTotalRecords', operator: '=', value: 'true' },
      { columnName: 'search_word', operator: '=', value: keyword }
    ];
  }

  /**
   * Reset the search bar.
   */
  reset() {
    this.searchValue = '';
    const datasource = this.getDatasource();
    this.gridApi.setServerSideDatasource(datasource);
  }

  /**
   * This function will add new teams to the role.
   *
   */
   onAdd() {
     const guids = [...this.selectedEntities.keys()];
     // clear selected entities
     this.selectedEntities.clear();
     // dispatch ngrx action to add teams to the role
     this.store.dispatch(RolePermActions.addTeamsToRoleInit({teamGuids: guids, roleGuid: this.roleGuid}));
     // set form to disable again
     this.formDisabled = true;
  }

  /**
   * Return to the previous drawer/view column.
   */
   onReturn() {
    this.viewColFacade.updateInstance(this.prevIndex, {
      ...this.prevLocalState,
      deactivateReturn: false,
      deactivateAdd: false,
      deactivateList: false
    });
    this.viewColFacade.onPrev(this.prevIndex);
  }

  /**
   * Lifecycle method: Called when this component is destroyed.
   */
   ngOnDestroy() {
    if (this.matTab) {
      this.viewColFacade.updateInstance(this.index, {
        ...this.localState,
        selectedIndex: this.matTab.selectedIndex
      });
    }
    this.subSink.unsubscribe();
  }

}
